import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { trackAhrefsPageview } from "@/lib/ahrefsAnalytics";
import {
  captureUTMAttributionPostHog,
  startPostHogGeoRefresh,
  trackPageViewPostHog,
  trackScrollDepthPostHog,
  trackTimeOnPagePostHog,
  waitForPostHogGeoEnrichment,
} from "@/lib/posthogAnalytics";
import { getPageEngagementContext } from "@/lib/posthogEngagementContext";
import { isClerkAdmin } from "@/lib/auth";
import {
  applyPostHogSessionPolicy,
  isPostHogDisabledPath,
} from "@/lib/posthogPathGuard";
import { identifyUserPostHog } from "@/lib/posthogAnalytics";
import { identifyDataFastUser } from "@/lib/datafastAnalytics";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { useClerkGateMounted } from "@/lib/clerkGate";
import { useUser } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Tracks pageviews and route_change for PostHog.
 * Also handles: scroll depth (25/50/75/90/100%), time on page,
 * UTM attribution, and route slug enrichment.
 */

const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];

/** Extract slug info from dynamic route patterns */
function extractSlug(pathname: string): { route_type: string; slug?: string } | null {
  const vipMatch = pathname.match(/^\/vip\/([^/]+)/);
  if (vipMatch) return { route_type: "vip", slug: vipMatch[1] };

  const pMatch = pathname.match(/^\/p\/([^/]+)/);
  if (pMatch) return { route_type: "product", slug: pMatch[1] };

  const buyMatch = pathname.match(/^\/buy\/([^/]+)/);
  if (buyMatch) return { route_type: "direct_checkout", slug: buyMatch[1] };

  const reelMatch = pathname.match(/^\/reel\/([^/]+)/);
  if (reelMatch) return { route_type: "reel", slug: reelMatch[1] };

  const giftMatch = pathname.match(/^\/gift\/([^/]+)/);
  if (giftMatch) return { route_type: "gift", slug: giftMatch[1] };

  return null;
}

// Constante de build: sin publishable key no se monta <ClerkProvider>
// (ver AppClerkProvider en App.tsx), así que useUser() lanzaría y tumbaría
// el sitio público vía ErrorBoundary. Gateamos por esta constante.
const CLERK_ENABLED = Boolean(getClerkPublishableKey());

type PageviewUser = ReturnType<typeof useUser>["user"];

export function PostHogPageview() {
  // Gate dinámico: en la landing el provider se difiere (perf mobile) —
  // hasta que monte, comportarse como sin-Clerk (ver src/lib/clerkGate.tsx).
  const clerkMounted = useClerkGateMounted();
  return CLERK_ENABLED && clerkMounted ? (
    <PostHogPageviewClerk />
  ) : (
    <PostHogPageviewImpl user={null} isClerkLoaded />
  );
}

function PostHogPageviewClerk() {
  const { user, isLoaded } = useUser();
  return <PostHogPageviewImpl user={user ?? null} isClerkLoaded={isLoaded} />;
}

function PostHogPageviewImpl({
  user,
  isClerkLoaded,
}: {
  user: PageviewUser;
  isClerkLoaded: boolean;
}) {
  const location = useLocation();
  const { templateName } = useCurrentTemplate();
  const isHome = location.pathname === "/";
  const isPostHogDisabled = isPostHogDisabledPath(location.pathname);

  // Refs so cleanup closures always see current values
  const pageEnterTime = useRef<number>(Date.now());
  const scrollDepthReached = useRef<Set<number>>(new Set());
  const disabledRef = useRef<boolean>(isPostHogDisabled);
  disabledRef.current = isPostHogDisabled;

  // Replay: anónimo + role user sí; /admin y Clerk admin no.
  // Ruta se aplica al instante; rol admin solo cuando Clerk terminó de cargar.
  useEffect(() => {
    applyPostHogSessionPolicy(
      location.pathname,
      isClerkLoaded ? (user ?? null) : null,
    );
  }, [location.pathname, isClerkLoaded, user]);

  useEffect(() => {
    if (!isClerkLoaded || !user || isClerkAdmin(user) || isPostHogDisabled) return;
    identifyUserPostHog(user.id, {
      role: "user",
      clerk_role: (user.publicMetadata as { role?: string })?.role ?? "user",
    });
    // DataFast identify → searchable visitor profiles + better attribution
    // https://datafa.st/docs/user-identification
    const email = user.primaryEmailAddress?.emailAddress ?? undefined;
    identifyDataFastUser({
      userId: user.id,
      email: email ?? null,
      name: user.fullName || user.firstName || null,
      image: user.imageUrl || null,
      extra: {
        role: "user",
        clerk_role: (user.publicMetadata as { role?: string })?.role ?? "user",
      },
    });
  }, [location.pathname, isClerkLoaded, user, isPostHogDisabled]);

  // ── Scroll depth tracking (rAF-throttled) ───────────────────────────────
  useEffect(() => {
    if (isPostHogDisabled) return;

    scrollDepthReached.current = new Set();
    let rafId: number | null = null;

    const checkScroll = () => {
      rafId = null;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.round((scrollTop / docHeight) * 100);
      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !scrollDepthReached.current.has(threshold)) {
          scrollDepthReached.current.add(threshold);
          trackScrollDepthPostHog(threshold);
        }
      }
    };

    const handleScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(checkScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [location.pathname, isPostHogDisabled]);

  // ── Time on page + visibilitychange ─────────────────────────────────────
  // - On route change: fire time_on_page in cleanup
  // - On tab hide: fire page_hidden with partial time (user may not come back)
  useEffect(() => {
    pageEnterTime.current = Date.now();

    const handleVisibility = () => {
      if (disabledRef.current || typeof window === "undefined" || !window.posthog) return;
      const seconds = Math.round((Date.now() - pageEnterTime.current) / 1000);

      if (document.visibilityState === "hidden") {
        window.posthog.capture("page_hidden", {
          time_active_seconds: seconds,
          ...getPageEngagementContext({ time_active_seconds: seconds }),
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (disabledRef.current) return;
      const seconds = Math.round((Date.now() - pageEnterTime.current) / 1000);
      if (seconds >= 2) trackTimeOnPagePostHog(seconds);
    };
  }, [location.pathname]);

  // ── Pageview + route_change + UTM attribution ────────────────────────────
  useEffect(() => {
    // Ahrefs Web Analytics (privacy, no cookies) — SPA pageviews for all public routes
    if (!isPostHogDisabled) {
      trackAhrefsPageview();
    }

    if (isPostHogDisabled) return;

    let cancelled = false;
    startPostHogGeoRefresh();

    const template = isHome ? templateName : null;

    void (async () => {
      await waitForPostHogGeoEnrichment();
      if (cancelled) return;
      trackPageViewPostHog(location.pathname, document.title, template);

      // Fire our custom utm_attribution event when UTMs are present.
      // PostHog JS already sets utm_* person properties automatically.
      captureUTMAttributionPostHog(location.search, location.pathname);

      if (typeof window !== "undefined" && window.posthog) {
        const slugInfo = extractSlug(location.pathname);

        const routePayload: Record<string, unknown> = {
          // Navigation context (not auto-captured by PostHog)
          from_path: document.referrer
            ? (() => {
                try { return new URL(document.referrer).pathname; } catch { return null; }
              })()
            : null,
          to_path: location.pathname,
          hash: location.hash,
          page_title: document.title,
          timestamp: new Date().toISOString(),
          // App-level custom properties
          user_region: localStorage.getItem("user_region") || "LATAM",
          test_country: localStorage.getItem("test_country"),
          // $set person properties inline — updates profile on each navigation
          $set: {
            user_region: localStorage.getItem("user_region") || "LATAM",
            test_country: localStorage.getItem("test_country") || null,
          },
        };

        if (template) routePayload.template = template;
        if (slugInfo) {
          routePayload.route_type = slugInfo.route_type;
          routePayload.slug = slugInfo.slug;
        }

        window.posthog.capture("route_change", routePayload);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Omit templateName to avoid duplicate pageview when template settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash, isHome, isPostHogDisabled]);

  // ── template_view on "/" ─────────────────────────────────────────────────
  useEffect(() => {
    if (
      isPostHogDisabled ||
      !isHome ||
      !templateName ||
      typeof window === "undefined" ||
      !window.posthog
    )
      return;

    window.posthog.capture("template_view", {
      template: templateName,
      path: location.pathname,
      timestamp: new Date().toISOString(),
      user_region: localStorage.getItem("user_region") || "LATAM",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHome, templateName, location.pathname]);

  return null;
}
